import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
} from "react-native";
import { colors, fontFamily, radii } from "../theme";

// Lightweight dropdown select — a tappable field + a modal sheet of options.
// Used for the birthday month/year on both Add child and Edit child so the
// parent picks from a clean list instead of a sprawling chip grid. No native
// picker dependency required.
//
// Lives here rather than inside AddChildScreen because Edit needs exactly the
// same control; a second copy is how the two screens drifted apart in the first
// place.
export function SelectField({
  placeholder,
  value,
  options,
  onSelect,
}: {
  placeholder: string;
  value: string | null;
  options: { label: string; value: number }[];
  onSelect: (v: number) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TouchableOpacity
        style={styles.select}
        activeOpacity={0.7}
        onPress={() => setOpen(true)}
      >
        <Text
          style={[styles.selectValue, value === null && styles.selectPlaceholder]}
          numberOfLines={1}
        >
          {value ?? placeholder}
        </Text>
        <Text style={styles.selectChevron}>▾</Text>
      </TouchableOpacity>
      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <TouchableOpacity
          style={styles.selectBackdrop}
          activeOpacity={1}
          onPress={() => setOpen(false)}
        >
          <View style={styles.selectSheet}>
            <ScrollView showsVerticalScrollIndicator={false}>
              {options.map((opt) => {
                const active = opt.label === value;
                return (
                  <TouchableOpacity
                    key={opt.value}
                    style={[
                      styles.selectOption,
                      active && styles.selectOptionActive,
                    ]}
                    activeOpacity={0.7}
                    onPress={() => {
                      onSelect(opt.value);
                      setOpen(false);
                    }}
                  >
                    <Text
                      style={[
                        styles.selectOptionText,
                        active && styles.selectOptionTextActive,
                      ]}
                    >
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  select: {
    height: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 14,
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    borderRadius: radii.md - 2,
    backgroundColor: colors.surface,
  },
  selectValue: {
    flex: 1,
    fontFamily,
    fontSize: 16,
    fontWeight: "700",
    color: colors.ink,
  },
  selectPlaceholder: {
    color: colors.ink3,
    fontWeight: "600",
  },
  selectChevron: {
    fontFamily,
    fontSize: 14,
    fontWeight: "800",
    color: colors.ink2,
    marginLeft: 8,
  },
  selectBackdrop: {
    flex: 1,
    backgroundColor: "rgba(46,35,22,0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
  },
  selectSheet: {
    width: "100%",
    maxWidth: 300,
    maxHeight: 360,
    backgroundColor: colors.surface,
    borderRadius: radii.xl,
    paddingVertical: 6,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.2,
    shadowRadius: 28,
    elevation: 12,
  },
  selectOption: {
    paddingVertical: 13,
    paddingHorizontal: 20,
  },
  selectOptionActive: {
    backgroundColor: colors.bgWarm,
  },
  selectOptionText: {
    fontFamily,
    fontSize: 16,
    fontWeight: "700",
    color: colors.ink,
  },
  selectOptionTextActive: {
    color: colors.primary,
    fontWeight: "900",
  },
});

export default SelectField;
